/**
 * Global Privacy Control (GPC) Detection Utility
 *
 * Framework-agnostic helper that reads the two GPC surfaces page JS can
 * see: the standardized `navigator.globalPrivacyControl` property, and the
 * `as_gpc` cookie. Split out of `core.ts` so it can be unit-tested in
 * isolation and reused by any surface (vanilla bundle, React observer,
 * analytics gates) without pulling the whole consent core into scope.
 *
 * The `as_gpc` cookie is stamped by the Rails `GpcCookieConcern` from the
 * `Sec-GPC` request header of the request that delivered this document
 * (D8). It exists because a privacy proxy (DuckDuckGo's app-level proxy,
 * some VPNs, corporate egress) can announce the opt-out to the server
 * without ever setting `navigator.globalPrivacyControl`. It is a transport
 * for one request header, NOT a stored decision: it is never written into
 * or read from the `as_cookie_consent` payload, carries no version, and
 * nothing in the frontend writes it.
 *
 * Reference: https://globalprivacycontrol.github.io/gpc-spec/
 */

import { readRawCookie } from "./cookies";

export const GPC_COOKIE_NAME = "as_gpc";
export const GPC_COOKIE_AFFIRMATIVE = "1";

/**
 * Interpret a raw `as_gpc` cookie value. Any value other than the
 * affirmative literal means "not signalled": the cookie is machine-written
 * by the server and exact, so there is no truthy-string guessing to do.
 *
 * @param raw - the raw cookie value, or `null` when absent
 * @returns `true` only for `"1"`
 */
export function readGpcCookieSignal(raw: string | null): boolean {
  return raw === GPC_COOKIE_AFFIRMATIVE;
}

/**
 * The navigator half. Returns `false` in non-browser environments.
 *
 * @returns `true` when `navigator.globalPrivacyControl === true`
 */
function navigatorGpc(): boolean {
  if (typeof navigator === "undefined") return false;
  return (
    (navigator as Navigator & { globalPrivacyControl?: boolean })
      .globalPrivacyControl === true
  );
}

/**
 * The cookie half. Returns `false` in non-browser environments.
 *
 * @returns `true` when the `as_gpc` cookie is `"1"`
 */
function cookieGpc(): boolean {
  if (typeof document === "undefined") return false;
  return readGpcCookieSignal(readRawCookie(GPC_COOKIE_NAME));
}

// Memoized for the lifetime of the module — i.e. for one page load. Several
// call sites read this on every load and they must never disagree: the
// cookie half reads live `document.cookie`, which a subresource response
// arriving between two calls can change underneath them. A divergence there
// is silent and one-directional — one caller denies advertising, the next
// reads `false` and renders the Advertising toggle enabled on a load that has
// already told the tags the visitor opted out. A per-load answer is also the
// honest one: the server stamps the cookie from the request that delivered
// this document, so the signal describes this load and nothing later.
let cached: boolean | null = null;

/**
 * Detect whether the current browser is broadcasting Global Privacy
 * Control. The navigator property is checked first; the cookie is the
 * fallback. Either surface alone is sufficient and neither can turn the
 * other off.
 *
 * @returns `true` when either surface signals GPC, memoised per page load
 */
export function detectGpc(): boolean {
  cached ??= navigatorGpc() || cookieGpc();
  return cached;
}

/**
 * Test seam: return the memo to "not yet computed". A browser loads this
 * module once per page, so the memo is never observable there; a unit test
 * file loads it once for every case. Nothing in the application calls this.
 */
export function __resetGpcForTests(): void {
  cached = null;
}
